import React, { useState } from "react";
import { Table, Spinner } from "react-bootstrap";
import { Doughnut, Line } from "react-chartjs-2";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import { FaChartBar, FaSearch } from "react-icons/fa";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import CountryList from "../../CountryList";
import { googleApi } from "../../../services/api";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const AnalyticsAndReporting = () => {
  const [packageName, setPackageName] = useState("");
  const [country, setCountry] = useState("");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    if (!packageName || !country) {
      setError("Please fill in all fields.");
      return;
    }

    setLoading(true);
    try {
      const analyticsResponse = await googleApi.post("app-data/", {
        package_name: packageName,
        country,
      });

      setAnalyticsData(analyticsResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(
        error.response
          ? "Failed to fetch data. Please try again."
          : "Failed to make request."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const formatNumber = (num) => {
    const isPlusSignPresent = typeof num === "string" && num.includes("+");
    const cleanedNum =
      typeof num === "string" ? parseFloat(num.replace(/[^\d.-]/g, "")) : num;

    let formattedNum = cleanedNum;

    if (cleanedNum >= 1e9) {
      formattedNum = (cleanedNum / 1e9).toFixed(1) + "B";
    } else if (cleanedNum >= 1e6) {
      formattedNum = (cleanedNum / 1e6).toFixed(1) + "M";
    } else if (cleanedNum >= 1e3) {
      formattedNum = (cleanedNum / 1e3).toFixed(1) + "K";
    }

    return isPlusSignPresent ? formattedNum + "+" : formattedNum;
  };

  const downloadCSV = () => {
    const rows = [
      ["Total Installs", "Uninstall Rate", "Average Rating", "Total Reviews"],
      [
        analyticsData["Total Installs"],
        `${analyticsData["Estimated Uninstall Rate (rough)"]}%`,
        analyticsData["Average Rating"],
        analyticsData["Total Reviews"],
      ],
    ];

    const escapeCSV = (val) => {
      const s = val === null || val === undefined ? "" : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const csv = "\uFEFF" + rows.map((r) => r.map(escapeCSV).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "analytics_report.csv");
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text("Analytics Report", 14, 20);

    autoTable(doc, {
      startY: 30,
      head: [["Total Installs", "Uninstall Rate", "Average Rating", "Total Reviews"]],
      body: [
        [
          analyticsData["Total Installs"],
          `${analyticsData["Estimated Uninstall Rate (rough)"]}%`,
          analyticsData["Average Rating"],
          analyticsData["Total Reviews"],
        ],
      ],
    });

    let y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || 30;
    y += 10;

    const pageWidth =
      doc.internal.pageSize.getWidth?.() ?? doc.internal.pageSize.width;
    const pageHeight =
      doc.internal.pageSize.getHeight?.() ?? doc.internal.pageSize.height;

    const addChartById = (canvasId, height = 90) => {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return;
      const img = canvas.toDataURL("image/png");
      const x = 15;
      const w = pageWidth - 30;

      if (y + height > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }
      doc.addImage(img, "PNG", x, y, w, height);
      y += height + 10;
    };

    addChartById("doughnut-chart");
    addChartById("line-chart");

    doc.save("analytics_report.pdf");
  };

  return (
    <div className="container mt-4">
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaChartBar className="mr-3 text-blue-500" />
          Analytics & Reporting
        </h2>
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-5">
                    <label htmlFor="packageName" className="block text-sm font-medium text-gray-700 mb-1">
                        Enter Package Name
                    </label>
                    <input
                        type="text"
                        className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                        id="packageName"
                        placeholder="e.g. com.example.app"
                        value={packageName}
                        onChange={(e) => setPackageName(e.target.value)}
                        required
                    />
                </div>
                <div className="md:col-span-5">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
                    <CountryList onSelect={(selectedCountry) => setCountry(selectedCountry)} />
                </div>
                <div className="md:col-span-2">
                    <button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        ) : (
                            <FaSearch className="mr-2" />
                        )}
                        {loading ? "Loading..." : "Get Data"}
                    </button>
                </div>
            </div>
        </form>
      </div>
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <Spinner animation="border" role="status" />
          <span className="ms-2">Loading Please Wait...</span>
        </div>
      )}

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {analyticsData && (
        <div className="mt-5">
          <h3 className="mb-3" style={{ fontSize: "0.7rem" }}>
            Analytics Data
          </h3>
          <Table striped bordered hover style={{ fontSize: "0.7rem" }}>
            <thead>
              <tr>
                <th>Total Installs</th>
                <th>Monthly Estimated Uninstall Rate</th>
                <th>Yearly Estimated Uninstall Rate</th>
                <th>Average Rating</th>
                <th>Total Reviews</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{formatNumber(analyticsData["Total Installs"])}</td>
                <td>
                  {analyticsData["Monthly Estimated Uninstall Rate"].toFixed(2)}
                </td>
                <td>
                  {analyticsData["Yearly Estimated Uninstall Rate"].toFixed(2)}
                </td>
                <td>{analyticsData["Average Rating"].toFixed(2)}</td>
                <td>{formatNumber(analyticsData["Total Reviews"])}</td>
              </tr>
            </tbody>
          </Table>

          {/* Charts */}
          <div className="row mt-5">
            <div className="col-md-6">
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "16px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  overflow: "auto",
                  maxHeight: "450px",
                }}
              >
                <h5 className="text-center">Uninstall Rate</h5>
                <Doughnut
                  id="doughnut-chart"
                  data={{
                    labels: ["Uninstalled", "Remaining"],
                    datasets: [
                      {
                        data: [
                          analyticsData["Estimated Uninstall Rate (rough)"],
                          100 -
                            analyticsData["Estimated Uninstall Rate (rough)"],
                        ],
                        backgroundColor: ["#FF6384", "#36A2EB"],
                        hoverBackgroundColor: ["#FF6384", "#36A2EB"],
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                      },
                    },
                  }}
                  style={{ width: "100%", height: "400px" }}
                />
              </div>
            </div>

            <div className="col-md-6">
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "16px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  overflow: "auto",
                  maxHeight: "450px",
                }}
              >
                <h5 className="text-center">Rating Trends</h5>
                <Line
                  id="line-chart"
                  data={{
                    labels: ["Phase 1", "Phase 2", "Phase 3", "Latest"],
                    datasets: [
                      {
                        label: "Average Rating",
                        data: [4.2, 4.3, 4.5, analyticsData["Average Rating"]],
                        fill: false,
                        borderColor: "#4BC0C0",
                        tension: 0.4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "bottom",
                      },
                    },
                  }}
                  style={{ width: "100%", height: "400px" }}
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button className="btn btn-success me-2" onClick={downloadPDF}>
              Download PDF
            </button>
            <button className="btn btn-secondary" onClick={downloadCSV}>
              Download CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsAndReporting;
